import { useEffect, useRef, useState } from 'react';

import { ARS_USER_PLATFORMS } from '@web-platform/shared-universal-constants/src/adverts';
import { INITIAL } from '@web-platform/shared-universal-constants/src/network';
import {
  RADAR_CREATE_SESSIONS,
  type QueryType,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import {
  getCulture,
  parseDateTime,
} from '@web-platform/shared-universal-utils-frontend-localisation';
import { useSelector } from 'react-redux';
import { getPreferences, PREFERENCES } from 'saddlebag-tracking-preferences';

import { getQuery, getSession, getUserIdentifiers } from '../../../state';
import { useQuery } from '../../internals/queryApi';
import { adsRetrievalService as requestTransform } from '../../requestTransforms/adsRetrievalService/adsRetrievalService';

import type {
  ArsUserPlatform,
  PAGE_TYPES,
} from '@web-platform/shared-universal-constants/src/adverts';
import type { CabinClass } from '@web-platform/shared-universal-constants/src/cabinClass';
import type { TripType } from '@web-platform/shared-universal-constants/src/tripTypes';
import type { NextRequestAt } from '@web-platform/shared-universal-types/src/redux/actions/nextRequestAt';
import type { QueryItem } from '@web-platform/shared-universal-types/src/redux/state/network/queries';
import type { StateType } from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';

const ADS_RETRIEVAL_SERVICE_TIMEOUT = 600;

type PlacementIds = Record<string, string>;

type Props = {
  type: QueryType;
  creativeId?: string;
  placementIds: PlacementIds;
  /**
   * Must stay consistent with `placementIds` — a `desktop.*` placement paired with `MOBILE_WEB`
   * (or the reverse) is a contract mismatch. Defaults to `MOBILE_WEB` for callers whose
   * placements are hardcoded mobile.
   */
  platform?: ArsUserPlatform;
  pageType: AdsRetrievalServiceVariables['targeting']['page_type'];
  targeting?: {
    origin?: string;
    destination?: string;
    partnerIds?: string[];
    passengers?: {
      adults: number;
      children: number;
    };
    tripType?: TripType;
    cabinClass?: CabinClass;
    outboundDate?: string;
    inboundDate?: string;
  };
  requestGuid?: string;
  isEnabled?: boolean;
  fpsSessionId?: string;
};

export type AdsRetrievalServiceQueryState = {
  moreDataExpected: boolean;
  timedOut: boolean;
};

export type AdsRetrievalServiceVariables = {
  requestGuid?: string;
  id_creative?: string;
  id_placements: PlacementIds;
  targeting: {
    culture_settings: {
      country: string;
      currency: string;
      locale: string;
    };
    page_type: (typeof PAGE_TYPES)[keyof typeof PAGE_TYPES];
    partner_ids?: string[];
    origin_id?: string;
    destination_id?: string;
    passengers?: {
      adult_count: number;
      child_count: number;
    };
    itinerary_type?: string;
    cabin_class?: string;
    search_start_timestamp?: { unix_time_millis: number; timezone_offset_mins: number };
    search_end_timestamp?: { unix_time_millis: number; timezone_offset_mins: number };
  };
  user_features: {
    is_optedin_for_personalised: boolean;
    platform: ArsUserPlatform;
  };
  fps_session_id?: string;
  viewId?: string;
};

type ItineraryType = 'ROUND' | 'ONE_WAY' | 'MULTI_CITY';

const itineraryType: Record<TripType, ItineraryType> = {
  RETURN: 'ROUND',
  ONE_WAY: 'ONE_WAY',
  MULTI_DESTINATION: 'MULTI_CITY',
};

type AdsRetrievalServiceQueryVariables = {
  requestGuid?: string;
  placementIds: PlacementIds;
  platform: ArsUserPlatform;
  creativeId?: string;
  pageType: Props['pageType'];
  targeting?: Props['targeting'];
  fpsSessionId?: string;
};

const getNextRequestAt = (query?: QueryItem, isEnabled: boolean = true): NextRequestAt =>
  (query && query.requestCount) || !isEnabled ? null : INITIAL;

const getAdsRetrievalServiceQueryVariables = (
  props: AdsRetrievalServiceQueryVariables,
): AdsRetrievalServiceVariables => {
  const {
    creativeId,
    fpsSessionId,
    pageType,
    placementIds,
    platform,
    requestGuid,
    targeting = {},
  } = props;
  const { currency, locale, market } = getCulture();
  const trackingPreferences = getPreferences(market);

  const outbound = targeting.outboundDate ? parseDateTime(targeting.outboundDate) : null;
  const inbound = targeting.inboundDate ? parseDateTime(targeting.inboundDate) : null;

  const variables: AdsRetrievalServiceVariables = {
    requestGuid,
    id_placements: placementIds,
    targeting: {
      origin_id: targeting.origin,
      destination_id: targeting.destination,
      culture_settings: {
        country: market,
        currency,
        locale,
      },
      page_type: pageType,
      partner_ids: targeting.partnerIds,
      passengers: targeting.passengers
        ? {
            adult_count: targeting.passengers.adults,
            child_count: targeting.passengers.children,
          }
        : undefined,
      itinerary_type: targeting.tripType ? itineraryType[targeting.tripType] : undefined,
      cabin_class: targeting.cabinClass,
      search_start_timestamp: outbound
        ? {
            unix_time_millis: outbound.getTime(),
            timezone_offset_mins: outbound.getTimezoneOffset(),
          }
        : undefined,
      search_end_timestamp: inbound
        ? {
            unix_time_millis: inbound.getTime(),
            timezone_offset_mins: inbound.getTimezoneOffset(),
          }
        : undefined,
    },
    user_features: {
      is_optedin_for_personalised: trackingPreferences[PREFERENCES.ADVERTS],
      platform,
    },
  };

  if (creativeId) {
    variables.id_creative = creativeId;
  }

  if (fpsSessionId) {
    variables.fps_session_id = fpsSessionId;
  }

  return variables;
};

const isMoreDataExpected = (query?: QueryItem) =>
  !query || query.inFlight || query.lastResponseAt === null;

export const useAdsRetrievalServiceCreativeData = ({
  creativeId,
  fpsSessionId,
  isEnabled,
  pageType,
  placementIds,
  platform = ARS_USER_PLATFORMS.MOBILE_WEB,
  requestGuid,
  targeting,
  type,
}: Props): AdsRetrievalServiceQueryState => {
  const userIdentifiers = useSelector(getUserIdentifiers);
  const serverViewId = userIdentifiers?.viewId ?? undefined;
  const sessionViewGuid = useSelector(
    (state: StateType) => getSession(state, RADAR_CREATE_SESSIONS)?.sessionViewGuid || undefined,
  );
  const viewId = sessionViewGuid || serverViewId;
  const variables = getAdsRetrievalServiceQueryVariables({
    placementIds,
    platform,
    creativeId,
    targeting,
    pageType,
    requestGuid,
    fpsSessionId,
  });
  const query = useSelector((state: StateType) => getQuery(state, type, variables));
  useQuery({
    type,
    variables,
    request: requestTransform({ ...variables, viewId }),
    nextRequestAt: getNextRequestAt(query, isEnabled),
  });
  const [timedOut, setTimedOut] = useState(false);
  const moreDataExpectedRef = useRef(false);
  const moreDataExpected = isMoreDataExpected(query);
  moreDataExpectedRef.current = moreDataExpected;
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (moreDataExpectedRef.current) {
        setTimedOut(true);
      }
    }, ADS_RETRIEVAL_SERVICE_TIMEOUT);

    return () => clearTimeout(timeout);
  }, []);
  return {
    moreDataExpected,
    timedOut,
  };
};
